<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate @submit.prevent="() => {}">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200px" flat color="transparent">
          <v-card-title class="mb-10">
            <FormHeader
              :loading="loading"
              :disabled="disabled"
              :link="'page-audioguides'"
              :title="getTitle ? `${getTitle} аудиогид` : `Создать аудиогид`"
            />
          </v-card-title>
          <v-tabs v-model="activeTab">
            <v-tab v-for="tab in $options.tabs" :key="tab.value">
              {{ tab.title }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <v-card-text>
                <v-container>
                  <PageContent>
                    <InputTitleSlug
                      :is-loading="loading || disabled"
                      :title="fields.title"
                      :title-error="validationErrors.title"
                      :slug="fields.slug"
                      :isSlugEnabled="!isNew"
                      :slug-error="validationErrors.slug"
                      @change:title="fields.title = $event"
                      @change:slug="fields.slug = $event"
                      @input:slug="$v.fields.slug.$touch"
                    />
                    <v-row>
                      <QuillEditorVue
                        :cols="12"
                        :title="'Описание'"
                        :content="fields.description"
                        :isShowErrorMessage="true"
                        :errorText="validationErrors.description"
                        :loading="loading"
                        :disabled="disabled"
                        light-mode
                        @input="fields.description = $event"
                        @onChangeContent="$emit('change:descriptionForMobile', { fields, $event })"
                      />
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <MediaFilePicker
                          label="Изображения"
                          :file-type="$options.mediaTypesByKeys.IMAGE"
                          :value="fields.images"
                          @change="fields.images = $event"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <SeoSettings
                          :data="data"
                          :loading="loading || disabled"
                          @on-input-seo="fields = Object.assign({}, fields, $event)"
                        />
                      </v-col>
                    </v-row>
                  </PageContent>
                </v-container>
              </v-card-text>
            </v-tab-item>
            <v-tab-item>
              <v-card-text>
                <PageContent>
                  <DynamicListWrapper
                    :multiple-creating="false"
                    :list="fields.points"
                    :error-messages="mixValidationErrors"
                    :available-items="$options.availablePoints"
                    :copy-clipboard="mixCopyListItemClipboard"
                    :options="$options.options"
                    @list-sort="mixListItemsReassort"
                    @add="mixCreateListItemHandler"
                    @option="mixListItemOptionHandler"
                    @option-manual="optionPointHandler"
                    @change:descriptionForMobile="changeDescriptionForMobileInPoint($event)"
                  >
                    <template #item="{ data }">
                      <component
                        :is="data.item.type"
                        :list-item="data.item"
                        :error-messages="mixDynamicListValidationErrors[data.itemIndex]"
                        @change="mixListItemChangeHandler($event, data.itemIndex)"
                        @change-item="
                          mixListItemChangeItemHandler($event, data.itemIndex)
                        "
                        @remove-item="
                          mixListItemRemoveItemHandler($event, data.itemIndex)
                        "
                      />
                    </template>
                  </DynamicListWrapper>
                </PageContent>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          :published-at="data.publishedAt"
          :author="data.author ? data.author.name : ''"
          :is-loading="loading"
          :is-submit-disabled="disabled"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'audioguide'"
          :sortOrder="fields.sortOrder"
          @delete="$emit('delete-audioguide')"
          @cancel="$router.push({ name: 'page-audioguides' })"
          @submit="submit"
          @update-translations="updateLanguages"
          @language-update="$emit('language-update', $event)"
          @update-sort-order="fields.sortOrder = Number($event)"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              title="Статус:"
              :disabled="loading"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
            <v-row v-if="!isNew" align="center">
              <v-col cols="6">
                <span>Посмотреть на сайте</span>
              </v-col>
              <v-col cols="6">
                <v-btn
                  icon
                  link
                  color="primary"
                  target="_blank"
                  :href="redirerectToWebPortal"
                  :loading="loading"
                  :disabled="disabled"
                  ><v-icon>mdi-web</v-icon></v-btn
                >
              </v-col>
            </v-row>
          </template>
          <template #body>
            <v-card flat>
              <v-card-subtitle class="grey--text"> Связи</v-card-subtitle>
              <v-card-text>
                <v-row no-gutters>
                  <Organizer
                    :profileType="fields.profileType"
                    :profileId="fields.profileId"
                    :profileTypeError="validationErrors.profileType"
                    :loading="loading"
                    :disabled="disabled"
                    :allowedProfilesProps="['GUIDE', 'TOURIST_AGENCY']"
                    @input:profile-type="fields.profileType = $event"
                    @input:profile-id="fields.profileId = $event"
                  />
                </v-row>
                <v-row no-gutters>
                  <v-col v-if="!!cities">
                    <span>Город</span>
                    <v-select
                      dense
                      outlined
                      no-data-text="Список пуст"
                      item-value="id"
                      item-text="title"
                      clearable
                      :loading="loading"
                      :disabled="disabled"
                      :value="fields.cityId"
                      :items="cities"
                      @change="fields.cityId = $event"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col>
                    <span>Виды передвижения*</span>
                    <v-select
                      dense
                      outlined
                      no-data-text="Список пуст"
                      item-value="id"
                      item-text="label"
                      id="modes"
                      multiple
                      :loading="loading"
                      :disabled="disabled"
                      :error-messages="validationErrors.modes"
                      :value="fields.modes"
                      :items="dictionariesTypes.audioguideMode"
                      @change="fields.modes = $event"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col>
                    <span>Категории аудиогида*</span>
                    <v-select
                      dense
                      outlined
                      id="categories"
                      no-data-text="Список пуст"
                      item-value="id"
                      item-text="label"
                      multiple
                      :loading="loading"
                      :disabled="disabled"
                      :error-messages="validationErrors.categories"
                      :value="fields.categories"
                      :items="dictionariesTypes.audioguideCategory"
                      @change="fields.categories = $event"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>
<script>
import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import validationRules from './validation-rules.js'
import { audioguidePointsList } from '@/utils/widgets'
import pointsParentMix from '@/mixins/points/pointsParent.mix.js'
import parentDynamicList from '@/mixins/dynamic-list/parentDynamicList.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'
import DynamicListWrapper from '@/components/dynamic-list-wrapper/DynamicListWrapper.vue'
import MediaFilePicker from '@/components/media-file-picker'
import SeoSettings from '@/components/seo-settings'
import Geolocation from '@/utils/types/Geolocation.js'
import { AudioguidePoint } from '@/utils/widgets/widgetsTypes'
import Widget from '@/utils/widgets/Widget'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import QuillEditorVue from '@/components/quill-editor'
import profiles from '@/configs/profiles'
import StatusPicker from '@/components/status-picker'
import ProgressCircular from '@/components/progress-circular'
import Organizer from '@/components/organizer'

import { mixForm } from '@/utils/form.mix.js'
import PageContent from '@/components/page-content/PageContent.vue'
const tabs = [
  {
    title: 'Основное',
    value: 'main'
  },
  {
    title: 'Точки',
    value: 'points'
  }
]
export default {
  name: 'FormAudioguide',
  extends: FormAbstract,
  profiles,
  availablePoints: audioguidePointsList,
  dynamicListName: 'points',
  tabs,
  validationRules,
  mixins: [
    pointsParentMix,
    parentDynamicList,
    validationTranslations,
    prepareTranslations,
    mixForm
  ],
  mediaTypesByKeys: mediaTypesByKeys(),
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    dictionaries: {
      type: Array,
      default: () => []
    },
    cities: {
      type: Array,
      default: () => []
    },
    language: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  components: {
    PageContent,
    InputTitleSlug,
    TranslationIcon,
    ManageSidePanel,
    MediaFilePicker,
    DynamicListWrapper,
    SeoSettings,
    FormHeader,
    QuillEditorVue,
    StatusPicker,
    ProgressCircular,
    Organizer
  },
  data () {
    return {
      activeTab: 0,
      city: {},
      fields: {
        title: null,
        slug: undefined,
        status: null,
        description: '',
        modes: [],
        categories: [],
        points: [],
        images: [],
        cityId: null,
        profileId: undefined,
        profileType: undefined,
        seoMetaTitle: null,
        seoMetaDescription: null,
        seoDisallowIndex: false,
        seoMetaKeywords: null,
        seoRelCanonical: null,
        seoOgImage: null,
        translations: { ru: { isEnabled: true } },
        sortOrder: null
      },
      dictionariesTypes: {
        audioguideMode: [],
        audioguideCategory: []
      },
      postActions: {
        itemsToRemove: []
      },
      profiles: {
        TOURIST_AGENCY: [],
        GUIDE: []
      }
    }
  },
  computed: {
    redirerectToWebPortal () {
      if (this.data) {
        const { slug } = this.data
        return window.location.href.includes('demo')
          ? `${process.env.VUE_APP_WEB_PORTAL_URL_DEMO}/audio-guides/${slug}`
          : `${process.env.VUE_APP_WEB_PORTAL_URL}/audio-guides/${slug}`
      }
      return '#'
    },
    getTitle () {
      if (this.data) {
        return this.data?.title || ''
      }
      return null
    },
    validationRulesVars () {
      return {
        id: this.data?.id,
        ctx: this,
        isNew: this.isNew
      }
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.categories = data?.categories.map(c => c.id)
      this.fields.modes = data?.modes.map(c => c.id)
      this.fields.type = data?.type?.id
      this.prepareImages(data.images)
      this.preparePoints(data.points)
    },
    dictionaries (nevVal) {
      this.prepareDictionaries(nevVal)
    },
    fields: {
      deep: true,
      handler (newVal) {
        const clearProfileFields = () => {
          this.fields.profileId = null
          this.fields.profileType = null
        }
        const { profileId, profileType } = newVal

        if (this.fields.profileId !== profileId) {
          if (!profileId) {
            clearProfileFields()
          }
        }
        if (this.profileType !== profileType) {
          if (!profileType) {
            clearProfileFields()
          }
        }
      }
    }
  },
  methods: {
    updateLanguages (value) {
      Object.assign(this.fields.translations, value)
      this.fields.points = this.fields.points.map(point => {
        point.data.translations = value
      })
    },
    resetPostActions () {
      this.postActions.itemsToRemove = []
    },
    optionPointHandler ({ value }, itemIndex, item) {
      // TODO: attention! hardcoded value in the cases
      switch (value) {
        case 'delete':
          this.postActions.itemsToRemove.push(item.data.id)
          this.mixListItemRemoveItem(itemIndex)
          break
      }
    },
    prepareImages (images) {
      this.fields.images = images.map(image => image.id)
    },
    prepareDictionaries (list) {
      for (const type in this.dictionariesTypes) {
        const { options } = list.find(item => item.name === type) ?? {}
        this.dictionariesTypes[type] = options || []
      }
    },
    changeDescriptionForMobileInPoint ({ itemIndex, descForMob }) {
      this.fields.points[itemIndex].descriptionForMobile = descForMob
    },
    preparePoints (points) {
      this.fields.points = points.map(point => {
        const preparedPoint = {
          data: { ...point }
        }
        preparedPoint.data.images = point.images.map(image => image.id)
        preparedPoint.data.location = new Geolocation(point.location)
        preparedPoint.data.audioId = point?.audio?.id
        return Widget.populate(preparedPoint, AudioguidePoint)
      })
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      const postActions = { ...this.postActions }
      this.resetPostActions()
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }

      delete payload.seoOgImage
      delete payload.author
      this.mixPrepareTranslations(payload)
      const { points } = payload
      if (points?.length) {
        points.forEach(point =>
          point?.data?.translations ? this.mixPrepareTranslations(point.data) : point
        )
      }
      payload.points = points

      const isWidgetsInValid = this.mixValidateDynamicList(payload.points)
      if (!isWidgetsInValid) {
        this.$emit('submit', {
          data: payload,
          postActions
        })
      } else {
        this.$errorMsg('Не заполнены обязательные поля точек!')
      }
    }
  },
  mounted () {
    this.prepareDictionaries(this.dictionaries)
  }
}
</script>
